const YOUTUBE_PATTERNS = [
    /youtube\.com\/watch\?v=([\w-]{6,})/,
    /youtube\.com\/shorts\/([\w-]{6,})/,
    /youtube\.com\/embed\/([\w-]{6,})/,
    /youtu\.be\/([\w-]{6,})/
];

const INSTAGRAM_PATTERN = /instagram\.com\/(?:[\w.-]+\/)?(p|reels?)\/([\w-]+)/;

const TIKTOK_PATTERN = /tiktok\.com\/@[\w.-]+\/video\/(\d+)/;

export const SOCIAL_PLATFORM_LABELS = {
    youtube: "YouTube",
    instagram: "Instagram",
    tiktok: "TikTok"
};

export const SOCIAL_PLATFORM_ICONS = {
    youtube: "▶️",
    instagram: "📷",
    tiktok: "🎵"
};

export const SOCIAL_PLATFORM_OPTIONS = [
    { value: "youtube", label: "YouTube" },
    { value: "instagram", label: "Instagram" },
    { value: "tiktok", label: "TikTok" }
];

export const SOCIAL_DISPLAY_MODE_OPTIONS = [
    { value: "single", label: "Único" },
    { value: "multiple", label: "Múltiple" }
];

const MULTI_DISPLAY_PLATFORMS = new Set(["instagram", "tiktok"]);

export function supportsMultipleDisplay(platform) {
    return MULTI_DISPLAY_PLATFORMS.has(platform);
}

export function detectSocialEmbed(rawUrl) {
    const url = (rawUrl || "").trim();
    if (!url) return null;

    for (const pattern of YOUTUBE_PATTERNS) {
        const match = url.match(pattern);
        if (match) {
            return {
                platform: "youtube",
                embedUrl: `https://www.youtube.com/embed/${match[1]}`,
                videoId: match[1],
                aspectRatio: "16 / 9"
            };
        }
    }

    const instagramMatch = url.match(INSTAGRAM_PATTERN);
    if (instagramMatch) {
        const isReel = instagramMatch[1] !== "p";
        const pathSegment = instagramMatch[1] === "p" ? "p" : "reel";
        const shortcode = instagramMatch[2];
        return {
            platform: "instagram",
            // El endpoint /embed/ (sin el <script> del oEmbed oficial) es un
            // iframe puro: evita el bug ya documentado de que el script de
            // Instagram mutaba el DOM fuera de Vue y rompía el editor. Ese
            // script era también lo único que traía auto-resize — sin él,
            // la altura tiene que estimarse a mano. Sin /captioned/ (más
            // compacto: cabecera + foto/video + acciones, sin el bloque de
            // caption) el aspect-ratio sigue siendo algo más alto que el de
            // la foto sola por la cabecera/acciones fijas, pero bastante
            // menos que con caption. Se sobreestima a propósito (mejor
            // espacio vacío que contenido cortado) y se deja scroll interno
            // como red de seguridad.
            embedUrl: `https://www.instagram.com/${pathSegment}/${shortcode}/embed/`,
            videoId: shortcode,
            aspectRatio: isReel ? "1 / 2.6" : "1 / 1.9"
        };
    }

    const tiktokMatch = url.match(TIKTOK_PATTERN);
    if (tiktokMatch) {
        return {
            platform: "tiktok",
            embedUrl: `https://www.tiktok.com/player/v1/${tiktokMatch[1]}`,
            videoId: tiktokMatch[1],
            aspectRatio: "9 / 16"
        };
    }

    return null;
}

// TikTok expone un oEmbed público (sin token, sin límite de cuota conocido)
// que devuelve el thumbnail real del video.
export async function fetchTiktokThumbnail(rawUrl) {
    const url = (rawUrl || "").trim();
    if (!url) return null;

    const response = await fetch(
        `https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`
    );
    if (!response.ok) return null;

    const data = await response.json();
    return {
        thumbnailUrl: data.thumbnail_url || null,
        title: data.title || null
    };
}

// Instagram no publica ninguna API oficial que devuelva una imagen suelta —
// su oEmbed (sin token desde jun-2026) solo trae el <blockquote> con su
// propio script, que es justo lo que rompía el editor (ver MediaSegment.vue
// para el modo "único"). microlink.io es un servicio de "link preview" de
// terceros con capa gratuita (25 solicitudes/día sin API key) que sí
// resuelve la imagen real del post público. Se llama una sola vez al
// configurar el bloque en el editor — no en cada visita al catálogo — y el
// resultado se guarda en el segmento, así que la cuota gratuita alcanza.
export async function fetchInstagramThumbnail(rawUrl) {
    const url = (rawUrl || "").trim();
    if (!url) return null;

    const response = await fetch(
        `https://api.microlink.io/?url=${encodeURIComponent(url)}`
    );
    if (!response.ok) return null;

    const { data } = await response.json();
    return {
        thumbnailUrl: data?.image?.url || null,
        title: data?.title || null
    };
}
